import React, { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import imageIcon from "../../assets/images/icons/image_icon.svg";
import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import { uploadTournamentLogo } from "../../redux/slices/tournamentSlice";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { tournamentWizardStepCompleted } from "../../utils/analytics";
import { WIZARD_STEP } from "../../utils/analyticsEvents";
import "./TournamentLogo.css";

const TournamentLogo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const selectedTournament = useSelector(
    (state) => state.tournament.selectedTournament
  );
  const [validationError, setValidationError] = useState(null);

  const handleNext = async () => {
    try {
      if (selectedFile && selectedTournament) {
        setIsLoading(true);
        const formData = new FormData();
        formData.append("logo", selectedFile);

        const result = await dispatch(
          uploadTournamentLogo({
            tournamentId: selectedTournament.id,
            file: formData,
          })
        );

        if (result.meta.requestStatus === "fulfilled") {
          toast.success("Logo uploaded successfully!");
        } else {
          throw new Error("Failed to upload logo");
        }
      }

      tournamentWizardStepCompleted({
        stepName: WIZARD_STEP.TOURNAMENT_LOGO,
        tournamentId: selectedTournament?.id,
      });
      navigate(DASHBOARD_STACK.CREATE_TOURNAMENT.CHOOSE_SPORT);
    } catch (error) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setValidationError(null);

    if (!file) {
      setValidationError("Please select a file to upload.");
      return;
    }

    const validTypes = [
      "image/jpeg",
      "image/png",
      "image/svg+xml",
      "image/gif",
    ];
    if (!validTypes.includes(file.type)) {
      setValidationError(
        "Invalid file type. Please upload a SVG, PNG, JPG, or GIF image."
      );
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationError("File size too large. Maximum size is 5MB.");
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      setSelectedFile(file);
      setPreview(img.src);
    };
    img.onerror = () => {
      setValidationError("Invalid image file. Please try again.");
    };
  };

  return (
    <>
      <div className="input-section">
        <p>Add your competition logo</p>
        <div className="upload-container">
          <label htmlFor="file-upload" className="upload-area">
            {preview ? (
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: "200px" }}
              />
            ) : (
              <>
                <span className="upload-icon-container">
                  <img src={imageIcon} alt="Upload" className="upload-icon" />
                </span>
                <p className="upload-text-container">
                  <span className="upload-text">Click to upload </span> or drag
                  and drop
                </p>
                <p className="upload-format">
                  SVG, PNG, JPG or GIF (max. 800 x 400px)
                </p>
              </>
            )}
          </label>
          <input
            id="file-upload"
            type="file"
            onChange={handleFileChange}
            accept=".svg,.png,.jpg,.jpeg,.gif"
            className="file-input"
          />
          {validationError && (
            <div className="text-danger mt-2" style={{ fontSize: "0.9rem" }}>
              {validationError}
            </div>
          )}
        </div>
      </div>

      <ClubbieButton
        className="next-button"
        onClick={handleNext}
        disabled={isLoading || !!validationError}
        isLoading={isLoading}
      >
        Next
      </ClubbieButton>
    </>
  );
};

export default TournamentLogo;
